/**
 * ResetPinModal — Reset a worker's PIN.
 *
 * D-34: The worker's active mobile session is NOT invalidated on PIN reset.
 * The modal success flow shows PinShownOnceBanner with the newly-set PIN.
 * The PIN lives only in component state, not in localStorage / Query cache.
 */
import { type FormEvent, type SyntheticEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useResetPin } from "../../hooks/useResetPin";
import { TextField } from "../../components/TextField";
import { Button } from "../../components/Button";
import { Banner } from "../../components/Banner";
import { PinShownOnceBanner } from "./PinShownOnceBanner";

interface ResetPinModalProps {
  workerId: number;
  workerName: string;
  onClose: () => void;
}

export function ResetPinModal({ workerId, workerName, onClose }: ResetPinModalProps) {
  const intl = useIntl();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [pin, setPin] = useState("");
  const [pinFormatError, setPinFormatError] = useState("");
  const [revealedPin, setRevealedPin] = useState<string | null>(null);

  const resetPin = useResetPin();

  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      dialogRef.current?.close();
    };
  }, []);

  const handleClose = () => {
    setRevealedPin(null);
    onClose();
  };

  const handleDialogCancel = (e: SyntheticEvent) => {
    e.preventDefault();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setPinFormatError("");

    if (!/^\d{4}$/.test(pin)) {
      setPinFormatError(intl.formatMessage({ id: "ERR_VALIDATION_PIN_FORMAT" }));
      return;
    }

    const submittedPin = pin;
    resetPin.mutate(
      { worker_id: workerId, new_pin: submittedPin },
      {
        onSuccess: () => {
          setRevealedPin(submittedPin);
        },
      },
    );
  };

  const isServerError409 =
    resetPin.isError && resetPin.error?.code === "ERR_PIN_ALREADY_USED";

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleDialogCancel}
      data-testid="reset-pin-modal"
      className="rounded-lg border border-divider bg-surface-raised shadow-lg p-0 backdrop:bg-black/40 inline-size-[480px] max-inline-size-[calc(100vw-2rem)]"
    >
      {revealedPin ? (
        <div className="pbs-8 pbe-8">
          <PinShownOnceBanner pin={revealedPin} onClose={handleClose} />
        </div>
      ) : (
        <div className="ps-6 pe-6 pbs-6 pbe-6">
          <h2 className="text-xl font-semibold text-on-surface mbe-6">
            <FormattedMessage id="LABEL_RESET_PIN_FOR" values={{ name: workerName }} />
          </h2>

          {pinFormatError && (
            <div className="mbe-4">
              <Banner variant="error" data-testid="reset-pin-format-error-banner">
                {pinFormatError}
              </Banner>
            </div>
          )}

          {isServerError409 && (
            <div className="mbe-4">
              <Banner variant="error" data-testid="reset-pin-already-used-banner">
                <FormattedMessage id="ERR_PIN_ALREADY_USED" />
              </Banner>
            </div>
          )}

          {resetPin.isError && !isServerError409 && (
            <div className="mbe-4">
              <Banner variant="error">
                <FormattedMessage id="ERR_GENERIC" />
              </Banner>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <TextField
              id="reset-pin-new"
              label={intl.formatMessage({ id: "LABEL_NEW_PIN" })}
              value={pin}
              onChange={setPin}
              autoComplete="off"
              data-testid="reset-pin-input"
            />

            <div className="flex justify-end gap-3 mbs-8">
              <Button
                variant="secondary"
                type="button"
                onClick={handleClose}
                data-testid="reset-pin-cancel-button"
              >
                <FormattedMessage id="LABEL_CANCEL" />
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={resetPin.isPending}
                data-testid="reset-pin-submit-button"
              >
                <FormattedMessage id="LABEL_RESET_PIN" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </dialog>
  );
}
